import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  withStyles,
  Dialog,
  Divider as DividerMUI,
  FormControl,
  TextField,
  Typography as TypographyMUI,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';

import { Button as RSUIButton, useToast } from '@redshelf/redshelf-ui';

import { styled } from '@mui/material/styles';
import { Stack } from '@mui/material';
import CompareArrowsOutlinedIcon from '@mui/icons-material/CompareArrowsOutlined';
import Button from '@mui/material/Button';

import { SCREEN_SIZE_CATEGORIES } from 'constants';
import {
  AnalyticsModifiersContext,
  UserContentItemModifiersContext,
  UserContentValuesContext,
  ViewportValuesContext,
} from 'contexts';
import IconButton from 'components/IconButton';
import {
  AriaLiveRegion,
  Modal,
  ReadOnlyTextField,
  SelectFlashcardDeck,
} from 'components';
import { UserContentFlashcardDeck } from 'types';

const useStyles = makeStyles((theme) => {
  return {
    formControl: {
      paddingTop: theme.spacing(1),
      width: '100%',
    },
    textField: {
      display: 'flex',
      flex: '1 100%',
      margin: '8px',
      '& .MuiInputLabel-outlined': {
        backgroundColor: 'white',
        color: '#4D4D4D',
      },
    },
    readOnlyTextField: {
      '& div:last-child': {
        minHeight: '113px',
        borderColor: 'rgba(0, 0, 0, 0.23)',
      },
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      flex: '1',
      margin: '8px',
    },
    button: {
      margin: '8px',
    },
    dialogCloseButton: {
      alignSelf: 'flex-end',
    },
    title: {
      display: 'flex',
      flex: '1',
      alignSelf: 'center',
      paddingLeft: theme.spacing(1),
      fontSize: '1.175rem',
    },
    titleContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px',
    },
    flashcardModalBody: {
      padding: theme.spacing(0, 1),
    },
  };
});

const StyledDialog = withStyles((theme) => {
  return {
    paper: {
      width: '300px',
      margin: '0',
    },
  };
})(Dialog);

const StyledButton = styled((props) => (
  <Button focusVisibleClassName=".Mui-focusVisible" {...props} />
))(({ theme }) => ({
  '&.Mui-focusVisible': {
    outline: '2px solid black',
    outlineOffset: theme.spacing(0.25),
  },
}));

const FlashcardModal = ({
  anchorPosition,
  flashcard,
  onClose = () => {},
  onCloseAll = () => {},
  onSave = async () => {},
  shouldShowAsDialog, // Allow override for testing
  ...props
}) => {
  const classes = useStyles();
  const analytics = useContext(AnalyticsModifiersContext);
  const { isSaved, isSwapped, userId, snippet } = flashcard;

  const formattedSnippet = snippet.replace(/\s+/g, ' ').trim();
  const title = isSaved ? 'Edit a Flashcard' : 'Create a Flashcard';

  const { enqueueToast } = useToast();
  const toastMessage =
    "You've successfully created a flashcard. View your flashcards in the Flashcards area of the learning tools.";

  const { viewport } = useContext(ViewportValuesContext);
  const { flashcardDecks } = useContext(UserContentValuesContext);
  const { onDialogClose, onDialogOpen } = useContext(
    UserContentItemModifiersContext,
  );

  shouldShowAsDialog =
    shouldShowAsDialog !== undefined // Allow override for testing
      ? shouldShowAsDialog
      : viewport.widthCategory <= SCREEN_SIZE_CATEGORIES.TABLET;

  const [selectedDeck, setSelectedDeck] = useState(
    flashcardDecks.find(flashcard.inFlashcardDeck.bind(flashcard)),
  );
  const [deckSaving, setDeckSaving] = useState(false);

  const [textInput, setTextInput] = useState(flashcard?.userText);

  const [isBackOfFlashcardActive, setIsBackOfFlashcardActive] = useState(
    isSwapped ?? false,
  );

  const [isSaving, setIsSaving] = useState(false);

  const [ariaLiveMessage, setAriaLiveMessage] = useState(null);

  const handleClose = () => {
    onDialogClose('flashcardModal');
    onClose();
  };

  const handleSave = async () => {
    setIsSaving(true);

    await onSave({
      deckKey: selectedDeck.key,
      userText: textInput,
      isSwapped: isBackOfFlashcardActive,
    });

    analytics.flashcardCreated();

    setIsSaving(false);

    if (!flashcard?.id) {
      enqueueToast(toastMessage, {
        variant: 'success',
      });
    }

    onDialogClose('flashcardModal');
    onCloseAll();
  };

  const handleTextInputOnChange = (event) => {
    setTextInput(event.target.value);
  };

  const handleCancelOnClick = () => {
    setTextInput('');
    setSelectedDeck(null);
    handleClose();
  };

  const flashcardIsSaving = isSaving || deckSaving;

  const hasEmptyField = !textInput || !selectedDeck;

  const dialogTitle = (
    <>
      <div className={classes.titleContainer}>
        <TypographyMUI
          id="flashcard-dialog-title"
          className={classes.title}
          variant="h2"
        >
          {title}
        </TypographyMUI>
        <IconButton
          aria-label="Close"
          className={classes.dialogCloseButton}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
      </div>
      <DividerMUI />
    </>
  );

  const selectFlashcardDeckForm = (
    <FormControl variant="outlined" className={classes.formControl}>
      <SelectFlashcardDeck
        defaultValue={selectedDeck}
        flashcardDecks={flashcardDecks}
        onSelect={setSelectedDeck}
        onNewDeck={async (userText) => {
          setDeckSaving(true);

          const newFlashcardDeck = new UserContentFlashcardDeck({
            book: flashcard.bookLocation.book,
            userText,
            userId: userId,
          });
          newFlashcardDeck.save();

          setSelectedDeck(newFlashcardDeck);

          setDeckSaving(false);

          return newFlashcardDeck;
        }}
      />
    </FormControl>
  );

  const swapTextFields = (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ paddingTop: '4px', paddingBottom: '4px' }}
    >
      <StyledButton
        disableElevation
        disableFocusRipple
        onClick={() => {
          setIsBackOfFlashcardActive(!isBackOfFlashcardActive);

          // set state updates have not applied yet, use opposite value
          setAriaLiveMessage(
            `${isBackOfFlashcardActive ? 'front' : 'back'} of flashcard active`,
          );
        }}
        size="large"
        startIcon={<CompareArrowsOutlinedIcon />}
        variant="contained"
      >
        Switch Front and Back
      </StyledButton>
    </Stack>
  );

  const buttonContainer = (
    <div className={classes.buttonContainer}>
      <RSUIButton
        className={classes.button}
        id="flashcard-modal-cancel-button"
        onClick={handleCancelOnClick}
        variant="secondary"
      >
        Cancel
      </RSUIButton>
      <RSUIButton
        className={classes.button}
        disabled={flashcardIsSaving || hasEmptyField}
        id="flashcard-modal-save-button"
        onClick={handleSave}
      >
        Save
      </RSUIButton>
    </div>
  );

  const flashcardModalBody = (
    <div className={classes.flashcardModalBody}>
      {selectFlashcardDeckForm}
      <>
        {isBackOfFlashcardActive ? (
          <ReadOnlyTextField
            className={`${classes.readOnlyTextField} noTranslate`}
            content={formattedSnippet}
            id="front-of-flashcard"
            label="Front of Flashcard"
          />
        ) : (
          <TextField
            id="front-of-flashcard"
            className={classes.textField}
            label="Front of Flashcard"
            multiline
            onChange={handleTextInputOnChange}
            rows={4}
            value={textInput}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            inputProps={{ className: 'noTranslate' }}
          />
        )}
        {swapTextFields}
        {isBackOfFlashcardActive ? (
          <TextField
            id="back-of-flashcard"
            className={classes.textField}
            label="Back of Flashcard"
            multiline
            onChange={handleTextInputOnChange}
            rows={4}
            value={textInput}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            inputProps={{ className: 'noTranslate' }}
          />
        ) : (
          <ReadOnlyTextField
            className={`${classes.readOnlyTextField} noTranslate`}
            content={formattedSnippet}
            id="back-of-flashcard"
            label="Back of Flashcard"
          />
        )}
        <AriaLiveRegion id="flashcard-modal-toggle" message={ariaLiveMessage} />
      </>
    </div>
  );

  return (
    <>
      {shouldShowAsDialog || !anchorPosition ? (
        <StyledDialog
          aria-labelledby="flashcard-dialog-title"
          {...props}
          onClose={(_, reason) => {
            // default MUI behaviour is to close on Escape key Down, but we want Dialog to close on keyUp not keyDown
            // so prevent close on KeyDown
            if (reason !== 'escapeKeyDown') {
              handleClose();
            }
          }}
          // add close on Escape Key Up
          onKeyUp={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') {
              handleClose();
            }
          }}
          open={true}
          TransitionProps={{
            onEnter: () => onDialogOpen('flashcardModal'),
          }}
          disablePortal
        >
          {dialogTitle}
          {flashcardModalBody}
          {buttonContainer}
        </StyledDialog>
      ) : (
        <Modal
          anchorPosition={anchorPosition}
          onClose={(_, reason) => {
            // default MUI behaviour is to close on Escape key Down, but we want Dialog to close on keyUp not keyDown
            // so prevent close on KeyDown
            if (reason !== 'escapeKeyDown') {
              handleClose();
            }
          }}
          onKeyUp={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') {
              handleClose();
            }
          }}
          title={title}
          TransitionProps={{
            onEnter: () => onDialogOpen('flashcardModal'),
          }}
          {...props}
        >
          {flashcardModalBody}
          {buttonContainer}
        </Modal>
      )}
    </>
  );
};

FlashcardModal.propTypes = {
  anchorPosition: PropTypes.object,
  flashcard: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onCloseAll: PropTypes.func,
  onSave: PropTypes.func,
};

export default FlashcardModal;
